import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { 
  ScreenComponent,
  TopBarComponent, 
  MenuComponent,
  CarouselComponent,
  ArrowsComponent,
  MobileMenuComponent,
  ToggleIconComponent,
  menuSlide,
  FulltextSearchComponent
} from '@rx-lib/ui';
import { IconComponent, IconSetComponent } from '@rx-lib/ui';
import { SwipeNavigateDirective, SwipeNavigationService } from '@rx-lib/ui-gestures';
import { ANIMATION } from '@rx-lib/ui';
import { routeAnimation, fadeAnimation, slideOutAnimation } from '@rx-lib/ui'
import { MetaService, mapToMetaConfig } from '@rx-lib/meta';
import { environment } from './config/environment';
import { imageStyles } from './config/image_styles';

import { RxLet } from '@rx-angular/template/let';
import { STORE } from './app.store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { RxPush } from '@rx-angular/template/push';
import { rxEffects } from '@rx-angular/state/effects';
import { slideAnimation } from '@rx-lib/ui-gestures';
import { createCircularRouteAnimationParams$ } from '@rx-lib/navigation';
import { KeyboardRouteNavigationDirective } from '@rx-lib/navigation';
import { submenuState } from 'libs/ui/src/lib/menu/submenu-state.animation';


@Component({
  standalone: true,
  imports: [
    RouterOutlet, 
    ScreenComponent,
    TopBarComponent,
    MenuComponent,
    CarouselComponent,
    IconSetComponent,
    IconComponent,
    ArrowsComponent,
    MobileMenuComponent,
    ToggleIconComponent,
    RxLet,
    RxPush,
    SwipeNavigateDirective,
    KeyboardRouteNavigationDirective,
    FulltextSearchComponent,
    RouterLinkActive,
    RouterLink
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [routeAnimation, fadeAnimation, slideOutAnimation, slideAnimation, menuSlide, submenuState],
})
export class AppComponent {

  private duration = ANIMATION.duration;
  
  private store = inject(STORE);
  readonly currentRoute$ = this.store.select('currentRoute');
 
  /* ROUTE ANIMATION */

  readonly animationIndex = signal(0);

  readonly routeAnimationParams$ = createCircularRouteAnimationParams$(
    this.currentRoute$,
    this.store.select('routesMap'),
    {duration: this.duration, y: '0px'}
  ).pipe(
    tap((params) => console.log('Animation params', params))
  );


  /* CAROUSEL */

  readonly media$ = this.currentRoute$.pipe(
    tap((route) => console.log('AppComponent route ', route)),
    map((currentRoute) => (currentRoute.page.media[currentRoute.subIndex])),
    // tap((media) => console.log('AppComponent media ', media))
  );

  /* MENU */

  readonly routes$ = this.store.select('routes').pipe(
    // tap((routes) => console.log('AppComponent routes ', routes))
  );

  /* CLOSE ALL SUBMENUS AND RESET SEARCH */

  readonly routeChanged$ = this.currentRoute$.pipe(
    map(() => void 0)
  );


  /* ARROWS */

  readonly navigation$ = this.store.select('navigation');


  /* SWIPE NAVIGATION */

  private swipeService = inject(SwipeNavigationService);
  private router = inject(Router);

  
  /* META TAGS, TITLE AND CANONICAL URL */

  private meta = inject(MetaService);


  /* MOBILE MENU */

  readonly menuOpen = signal(false);

  /* SEARCH OVERLAY */

  readonly searchOpen = signal(false);

  /* SEARCH */

  // readonly routesMap$ = this.store.select('routesMap');
  readonly pages$ = this.store.select('pages');

  @ViewChild(FulltextSearchComponent)
  private searchBox?: FulltextSearchComponent;


  /* EFECTS */

  readonly effects: ReturnType<typeof rxEffects> = rxEffects();

  constructor() {
    
    /* CLOSE MENU ON ROUTE CHANGE */
    this.effects.register(
      this.currentRoute$,
      () => this.menuOpen.set(false)
    );

    /* CLOSE SEARCH ON ROUTE CHANGE */
    this.effects.register(
      this.currentRoute$,
      () => this.searchOpen.set(false)
    );

    /* UPDATE META TAGS */
    this.effects.register(
      this.currentRoute$.pipe(
        map((route) => mapToMetaConfig(route, environment, imageStyles)),
        this.meta.updateTags()
      )
    );

    /* NAVIGATE ROUTER ON SWIPE EVENT */
    this.effects.register(
      this.swipeService.swipe$.pipe(
        // tap((direction) => console.log('AppComponent direction ', direction)),
        filter((direction) => direction !== null),
        withLatestFrom(this.currentRoute$),
        
        tap(([direction, currentRoute]) => {
          // console.log('AppComponent direction ', direction);
          // console.log('AppComponent currentRoute ', currentRoute);

          if (direction === 'left') {
            this.router.navigate([currentRoute.nextUrl])
          }
          else if (direction === 'right') {
            this.router.navigate([currentRoute.prevUrl])
          }
        })
      )
    );
  }

  onRouteAnimDone = () => this.swipeService.reset();
  onSearchAnimationDone = () => this.searchOpen() ? this.searchBox?.focus() : void 0;


  // menu and search are mutually exclusive

  toggleMenu = () => {
    this.menuOpen.set(!this.menuOpen());

    if(this.searchOpen()) {
      this.searchOpen.set(false);
    }
  }

  toggleSearch = () => {
    this.searchOpen.set(!this.searchOpen());
    if(this.menuOpen()) {
      this.menuOpen.set(false);
    }
  }
}
